import { useRef, ReactNode, useEffect } from 'react';
import { StyleProp, ViewStyle, View } from 'react-native';
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
    interpolateColor,
    withRepeat,
    FadeIn,
} from 'react-native-reanimated';
import Images from '_images';
import { WW } from '_utils';
import { smallestMargin, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CacheImage } from 'Components';
import { ExternalUserProfile } from 'types/User';

const RowUsersDisplay = ({
    users,
    nUsersToDisplay = 5,
    subtext,
    isLoading,
}: {
    users: ExternalUserProfile[];
    nUsersToDisplay?: number;
    subtext: ReactNode;
    isLoading: boolean;
}) => {
    const { theme } = useThemeContext();
    const loadingHeight = useRef<number | null>(null);
    // Sort users with avatar first
    const imageUsers = users.sort((a, b) => (a.avatar ? -1 : b.avatar ? 1 : 0));

    const avatarStyle = {
        width: 25,
        height: 25,
        borderWidth: 1,
        borderColor: theme.mainBackground,
        borderRadius: 20,
        overflow: 'hidden' as const,
    };

    return (
        <View
            style={{
                gap: smallestMargin,
                alignItems: 'center',
                flexDirection: 'row',
                flex: 1,
            }}
        >
            {isLoading ? (
                <>
                    <View
                        style={{ flexDirection: 'row' }}
                        onLayout={(event) => {
                            loadingHeight.current = event.nativeEvent.layout.height;
                        }}
                    >
                        {Array.from({ length: nUsersToDisplay }).map((_, index) => (
                            <BackgroundColorSkeletonShimmer
                                key={index}
                                style={[
                                    avatarStyle,
                                    {
                                        borderWidth: 1,
                                        borderColor: theme.mainBackground,
                                        marginLeft: index === 0 ? 0 : -5,
                                    },
                                ]}
                            />
                        ))}
                    </View>
                    <BackgroundColorSkeletonShimmer
                        style={{ width: WW * 0.3, height: subtitleFontSize * 1.5, borderRadius: 5 }}
                    />
                </>
            ) : (
                <>
                    <View style={{ flexDirection: 'row', minHeight: loadingHeight.current ?? undefined }}>
                        {imageUsers.map((user, index) => (
                            <Animated.View
                                key={user.id}
                                entering={FadeIn.duration(300)}
                                style={[
                                    avatarStyle,
                                    { marginLeft: index === 0 ? 0 : -5, backgroundColor: theme.mainBackground },
                                ]}
                            >
                                <CacheImage
                                    source={user.avatar ?? Images.defaultAvatar}
                                    style={{ width: 25, height: 25 }}
                                />
                            </Animated.View>
                        ))}
                    </View>
                    {subtext}
                </>
            )}
        </View>
    );
};

const BackgroundColorSkeletonShimmer = ({ style }: { style: StyleProp<ViewStyle> }) => {
    const progress = useSharedValue(0);
    const { theme } = useThemeContext();

    useEffect(() => {
        progress.value = withRepeat(withTiming(1, { duration: 1200 }), -1, true);
    }, [progress]);

    const animatedStyle = useAnimatedStyle(() => {
        const backgroundColor = interpolateColor(progress.value, [0, 1], [theme.mediumGrey, theme.mediumBackground]);
        return {
            backgroundColor,
        };
    });

    return <Animated.View style={[style, animatedStyle]} />;
};

export default RowUsersDisplay;
